/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles, Tooltip} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ExitIcon from '@material-ui/icons/Clear';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getDevice, getDevices, updateDevice} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  AccountLink,
  Device,
  DeviceMove,
  Link
} from '@ion/core/src/types/entities';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import Dropdown, {createOptions, Options} from '../components/menus/Dropdown';
import {RemoveDeviceNotification} from '../notifications/RemoveDeviceNotification';

/**
 * Defines the device chooser prop definitions ...
 */
export type DeviceChooserProps = {
  accountId: number;
  device?: Device;
  account?: Account | AccountLink;
  onChange?: (device: Device) => void;
  readOnly?: boolean;
  required?: boolean;
  validationError?: string;
  classes?: string;
  defaultValue?: Link;
  devicePrompt?: boolean;
  setRemovedDevice?: Function;
  setMovingToGroup?: Function;
  formState?: object;
  isEditMode?: boolean;
  setFormState?: Function;
  hasDetailedViewLink?: boolean;
  detailedViewLinkText?: string;
  onDetailedLinkClick?: Function;
  setIsDeviceRemoved?: boolean;
  movedToAccount?: DeviceMove;
  hasDeviceMoveOption?: boolean;
}

/**
 * Generates the device chooser dropdown labels and values based on
 * the provided devices.
 */
export const getDeviceOptions = (devices: Device[]): Options[] => {
  if (devices?.length > 0) {
    return createOptions({
      items     : devices,
      valProp   : 'id',
      labelProp : 'name',
      augmentor : (item: Device, option: {value: string; label: string;}) => {
        return {...option, disabled: !!item?.asset};
      }
    });
  }
  return [];
};

/**
 * Provides a utility which filters a list of devices for the
 * currently selected id.
 */
const getSelectedValue = (
  devices: Device[],
  selected: number
): Device => {
  return devices.find(({id}: Device) => id === selected);
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  },
  actionLabel : {
    minWidth : '120px'
  },
  dropdown : {
    width : '90%',
    float : 'left'
  },
  removeIcon : {
    width      : '10%',
    float      : 'right',
    paddingTop : '28px'
  }
}));

/**
 * @DeviceChooser
 *
 * The DeviceChooser provides a list of devices for the selected account or
 * user's default account for display within a dropdown list and
 * allows the user to select a Device from the list which will be returned to the
 * client as the selected Device.
 *
 * ```typescript
 *
 *     import {DeviceChooser} from '@ion/dls/src/editors/';
 *
 *     <DeviceChooser account={selectedAccount} onChange={setDevice} />
 *
 *     // read-only mode ...
 *     <DeviceChooser readOnly
 *       accountId={selectedAccount}
 *       />
 *
 *     // with default value and required check ...
 *     <DeviceChooser required
 *       device={Device}
 *       accountId={selectedAccount}
 *       />
 *
 *     // with callback subscription ...
 *     <DeviceChooser onChange={setDevice}
 *       accountId={selectedAccount}
 *       />
 *
 * ```
 *
 * @device           Allows for providing a default Device, generally used for
 *                   edits.
 * @accountId        Allows for providing an account id for which the device list
 *                   is fetched.
 * @onChange         Callback prop which will be invoked with the seleted Device.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @includeNone      When true (default), localized 'None' option will be displayed.
 * @defaultValue     Default Value of the field
 */
export const DeviceChooser = ({
  accountId,
  device,
  account,
  onChange,
  readOnly,
  required,
  classes,
  validationError,
  devicePrompt,
  setRemovedDevice,
  setMovingToGroup,
  formState,
  isEditMode,
  setFormState,
  defaultValue,
  hasDetailedViewLink = false,
  detailedViewLinkText = null,
  onDetailedLinkClick,
  setIsDeviceRemoved,
  movedToAccount,
  hasDeviceMoveOption = false
}: DeviceChooserProps) => {
  const _classes                  = useStyles();
  const [devices, setDevices]     = useState<Device[]>(null);
  const [isRemoveDevice, setIsRemoveDevice] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(accountId));
  const [selected, setSelected]   = useState<number>(device?.id || null);
  const [options, setOptions]     = useState<Options[]>([]);
  const hasMounted                = useHasMounted();

  // whenever the accountId changes, the devices are reloaded ...
  useEffect(() => {
    accountId && loadDevices(accountId);
  }, [accountId]);

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue.title);
    }
  }, [defaultValue]);

  // manages loading devices based on the current accountId prop ...
  const loadDevices = async function(accountId: number) {
    let results: Device[];
    setIsLoading(true);
    !defaultValue && setSelected(null);
    try {
      results = await getDevices(accountId);
    } catch (err) {
      error(`Device: Error occurred retrieving Devices for account ${accountId}`);
    } finally {
      if (hasMounted !== false) {
        setDevices(results);
        setOptions(getDeviceOptions(results));
        setIsLoading(false);
        selected && onChange?.(undefined);
      }
    }
  };

  const removeDevice = function() {
    setIsRemoveDevice(true);
  };

  const onConfirmRemoveDevice = async function(group: Account) {
    setSelected(null);
    setFormState({devices: {name: 'devices', value: null}});
    const device = formState?.devices?.value?.[0] || formState?.devices?.value;
    const deviceData = device && await getDevice(entityId(device?.href || device?._self?.href));
    setRemovedDevice(deviceData?.[0]);
    setMovingToGroup(group);
    setIsRemoveDevice(false);
    setIsDeviceRemoved(true);
  };

  const onCancelRemoveDevice = function() {
    setIsRemoveDevice(false);
  };

  return (
    <div className={classes || _classes.root}>
      {isRemoveDevice && devicePrompt && (
        <RemoveDeviceNotification show={true}
          title={translate('common:editors.delete.remove', {selected: formState?.devices?.value?.name || formState?.devices?.value?.[0]?.title})}
          account={account}
          actionLabel={
            <span className={_classes.actionLabel}>
              {
                isLoading
                  ? <ButtonSpinner />
                  : translate('common:editors.shared.continue')
              }
            </span>
          }
          dismissLabel={translate('common:editors.shared.cancel')}
          onConfirm={onConfirmRemoveDevice}
          onCancel={function() {
            onCancelRemoveDevice();
          }}
          movedToAccount={movedToAccount}
          hasDeviceMoveOption={hasDeviceMoveOption}/>
      )}
      <div className={_classes.dropdown}>
        <Dropdown required={required}
          disabled={readOnly || isLoading || options?.length === 0}
          hasError={Boolean(validationError)}
          options={options}
          value={!isLoading ? (selected || '') : translate('common:states.loading')}
          fieldLabel={translate('common:editors.device.label')}
          ctaLinkText={detailedViewLinkText}
          placeholder={translate('common:editors.device.placeholder')}
          preferPlaceholder={!isLoading && !selected}
          loading={isLoading}
          labelAsValue={false}
          hasCTALink={hasDetailedViewLink}
          onCTALinkClick={onDetailedLinkClick}
          onChange={onChange ? function({target}) {
            const id = target.value as unknown as number;
            setSelected(id);
            onChange(getSelectedValue(devices, id));
          } : null} />
      </div>
      <div className={_classes.removeIcon}>
        <Tooltip title={translate('common:editors.delete.deviceRemove')} placement='top'>
          <span>
            <IconButton disabled={!selected || readOnly} onClick={removeDevice}>
              <ExitIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default DeviceChooser;
